import { z } from 'zod'

export const COMMERCE_RETENTION_DAYS = null
export const commerceCartIdSchema = z.string().uuid()
export const COMMERCE_TIMEZONE = 'Europe/Berlin' as const

export const commerceRecommendationSurfaceSchema = z.enum(['home', 'cart', 'product'])
export const commerceRecommendationSectionSchema = z.enum(['homepage', 'serie', 'zubehoer', 'ergaenzung', 'alternativen', 'passend', 'purchased', 'other'])
export const commerceRecommendationContextSchema = z.object({
  surface: commerceRecommendationSurfaceSchema,
  sourceProductId: z.number().int().positive().optional(),
  sourceVariantKey: z.string().min(1).max(120).regex(/^[a-zA-Z0-9_.:-]+$/).optional(),
  section: commerceRecommendationSectionSchema.optional(),
}).strip()
export type CommerceRecommendationContext = z.infer<typeof commerceRecommendationContextSchema>

// Only completed catalogue searches belong in analytics. Drop common contact
// details rather than retaining them in a search-term report.
export const normalizeCommerceSearchQuery = (value: unknown): string | null => {
  if (typeof value !== 'string' || /[\u0000-\u001f]/.test(value)) return null
  const query = value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('de-DE')
  if (!query || query.length > 120 || /[@\r\n]|https?:|www\.|[\u0000-\u001f]/i.test(query)) return null
  if (/\b[a-z\d.-]+\.(?:de|com|net|org|eu|io|at|ch)\b/i.test(query)) return null
  const validGtin = /^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(query) && query.split('').reverse().reduce((sum, digit, index) => sum + Number(digit) * (index % 2 ? 3 : 1), 0) % 10 === 0
  if (!validGtin && /\d(?:[\s().+/-]*\d){6}/.test(query)) return null
  return query
}

export const commerceItemSchema = z.object({
  id: z.number().int().positive(),
  key: z.string().max(120).optional(),
  qty: z.number().int().min(1).max(999),
}).strip()

const baseEventSchema = z.object({
  host: z.string().trim().min(1).max(120),
  event: z.enum(['visit', 'add_to_cart', 'begin_checkout', 'purchase', 'search', 'recommendation_click', 'recommendation_add']),
  cartId: commerceCartIdSchema.optional(),
  eventId: z.string().regex(/^[a-zA-Z0-9_-]{8,100}$/).optional(),
  items: z.array(commerceItemSchema).max(100).optional(),
  recommendation: commerceRecommendationContextSchema.optional(),
  query: z.string().max(120).optional(),
  resultCount: z.number().int().min(0).max(1_000_000).optional(),
}).strip()
export type CommerceEventPayload = z.infer<typeof baseEventSchema>
export type CommerceEventName = CommerceEventPayload['event']
export type CommerceItem = z.infer<typeof commerceItemSchema>

export const commerceEventSchema = baseEventSchema
  .refine(value => value.event !== 'purchase', 'Purchases are recorded by the order service')
  .refine(value => !['add_to_cart', 'begin_checkout'].includes(value.event) || !!value.items?.length, 'Cart is required')
  .refine(value => !['recommendation_click', 'recommendation_add'].includes(value.event) || value.items?.length === 1, 'One recommended product is required')
  .refine(value => value.event !== 'recommendation_click' || value.items?.[0]?.qty === 1, 'A product click has quantity one')
  .refine(value => !value.recommendation || ['recommendation_click', 'recommendation_add'].includes(value.event), 'Recommendation context requires a recommendation event')
  .refine(value => value.event !== 'search' || (typeof value.resultCount === 'number' && normalizeCommerceSearchQuery(value.query) !== null), 'Completed catalogue search is required')
